import { useEffect, useState, useMemo } from "react";
import { Spin, message, Typography } from "antd";
import { useAppDispatch } from "redux/hooks";
import {
  getNumberList,
  getUserList,
  updateCoverPageAssignUser,
  updateCoverPageAssignNumber,
  deleteCoverPageAssignUser,
  deleteCoverPageAssignNumber,
  deleteCoverPage,
} from "redux/slice/coverPageSlice";
import { Modal, SelectBar } from "components/ui-component";
import { ConfirmationModal } from "components/ui-component/modals";
import DeleteIcon from "components/Icons/Delete";
import { ActionButton } from "components/ui-component/modals/FaxDetailsModal.style";

interface EditCoverPageModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  coverPage: {
    id: number;
    name: string;
    description: string;
    assigned_users: { id: number; first_name: string; last_name: string }[];
    assigned_numbers: { id: number; did: string }[];
  };
}

interface UserResponse {
  users: Array<{
    id: number;
    firstname: string;
    lastname: string;
  }>;
}

interface NumberResponse {
  numbers: Array<{
    id: number;
    did: string;
  }>;
}

const EditCoverPageModal = ({
  visible,
  onClose,
  onSuccess,
  coverPage,
}: EditCoverPageModalProps) => {
  const dispatch = useAppDispatch();

  // State for user/number lists
  const [userOptions, setUserOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [numberOptions, setNumberOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [loading, setLoading] = useState(false);

  // State for selected users/numbers
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([]);

  // State for delete modal and loading
  const [isDeleteModalVisible, setIsDeleteModalVisible] =
    useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  // Fetch lists on open
  useEffect(() => {
    const fetchData = async () => {
      if (visible) {
        setLoading(true);
        try {
          const [userRes, numberRes] = await Promise.all([
            dispatch(getUserList()).unwrap(),
            dispatch(getNumberList()).unwrap(),
          ]);

          const userResponse = userRes as any as UserResponse;
          const numberResponse = numberRes as any as NumberResponse;

          setUserOptions(
            (userResponse.users || []).map((u) => ({
              label: `${u.firstname} ${u.lastname}`,
              value: u.id,
            }))
          );
          setNumberOptions(
            (numberResponse.numbers || []).map((n) => ({
              label: n.did,
              value: n.id,
            }))
          );
        } catch (error) {
          message.error("Failed to fetch users or numbers");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [visible, dispatch]);

  // Set initial selections when modal opens or coverPage changes
  useEffect(() => {
    if (visible && coverPage) {
      setSelectedUsers(coverPage.assigned_users?.map((u) => u.id) || []);
      setSelectedNumbers(coverPage.assigned_numbers?.map((n) => n.id) || []);
    }
  }, [visible, coverPage]);

  // Memoize for quick lookup
  const prevUsers = useMemo(
    () => new Set(coverPage.assigned_users?.map((u) => u.id)),
    [coverPage]
  );
  const prevNumbers = useMemo(
    () => new Set(coverPage.assigned_numbers?.map((n) => n.id)),
    [coverPage]
  );

  const handleUserChange = async (newUserIds: number[]) => {
    try {
      // Update UI immediately
      setSelectedUsers(newUserIds);

      // Find the single change (either added or removed)
      const addedUser = newUserIds.find((id) => !prevUsers.has(id));
      const removedUser = Array.from(prevUsers).find(
        (id) => !newUserIds.includes(id)
      );

      if (addedUser) {
        await dispatch(
          updateCoverPageAssignUser({
            coverpage_id: coverPage.id,
            user_id: addedUser,
          })
        ).unwrap();
        // Update prevUsers set after successful addition
        prevUsers.add(addedUser);
        message.success("User added successfully");
      } else if (removedUser) {
        await dispatch(
          deleteCoverPageAssignUser({
            coverpage_id: coverPage.id,
            user_id: removedUser,
          })
        ).unwrap();
        // Update prevUsers set after successful removal
        prevUsers.delete(removedUser);
        message.success("User removed successfully");
      }

    } catch (error) {
      // Revert UI on error
      setSelectedUsers(Array.from(prevUsers));
      message.error("Failed to update user");
    }
  };

  const handleNumberChange = async (newNumberIds: number[]) => {
    try {
      // Update UI immediately
      setSelectedNumbers(newNumberIds);

      // Find the single change (either added or removed)
      const addedNumber = newNumberIds.find((id) => !prevNumbers.has(id));
      const removedNumber = Array.from(prevNumbers).find(
        (id) => !newNumberIds.includes(id)
      );

      if (addedNumber) {
        await dispatch(
          updateCoverPageAssignNumber({
            coverpage_id: coverPage.id,
            number_id: addedNumber,
          })
        ).unwrap();
        // Update prevNumbers set after successful addition
        prevNumbers.add(addedNumber);
        message.success("Number added successfully");
      } else if (removedNumber) {
        await dispatch(
          deleteCoverPageAssignNumber({
            coverpage_id: coverPage.id,
            number_id: removedNumber,
          })
        ).unwrap();
        // Update prevNumbers set after successful removal
        prevNumbers.delete(removedNumber);
        message.success("Number removed successfully");
      }

    } catch (error) {
      // Revert UI on error
      setSelectedNumbers(Array.from(prevNumbers));
      message.error("Failed to update number");
    }
  };

  const onDeleteCoverPage = async () => {
    setDeleteLoading(true);
    try {
      await dispatch(deleteCoverPage(coverPage.id)).unwrap();
      message.success("Cover Page deleted successfully");
      setIsDeleteModalVisible(false);
      onClose();
    } catch (error: any) {
      console.log("Delete Cover Page Err: --->", error?.message);
      message.error("Failed to delete cover page");
      setIsDeleteModalVisible(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={visible}
        onCancel={onClose}
        title={
          <div className="faxdata-bottom">
            <div className="faxdata-bottom__left">
              <Typography.Title level={5}>Cover Page Details</Typography.Title>
            </div>
            <div className="faxdata-bottom__right">
              <ActionButton
                size="small"
                icon={<DeleteIcon />}
                className="delete-icon"
                onClick={() => setIsDeleteModalVisible(true)}
              />
            </div>
          </div>
        }
        centered
        destroyOnClose
        closable={false}
        cancelButtonType="primary"
        isOkButton={false}
        className="cover-page-details-modal"
      >
        {loading ? (
          <div className="modal-spinner">
            <Spin />
          </div>
        ) : (
          <>
            <div className="cover-page-data-wrapper">
              <div className="cover-pag-name-block">
                <div className="head">Cover Page Name</div>
                <div className="detail">{coverPage.name}</div>
              </div>
              <div className="cover-pag-name-block">
                {" "}
                <div className="head">Description</div>
                <div className="detail">{coverPage.description}</div>
              </div>
            </div>
            {userOptions.length > 0 && (
              <div className="mb-4 font-14 my-10">
                <div className="font-14 my-5">Assigned Users</div>
                <SelectBar
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder="Select users"
                  value={selectedUsers}
                  onChange={handleUserChange}
                  options={userOptions}
                  optionFilterProp="label"
                  showSearch
                />
              </div>
            )}
            {numberOptions.length > 0 && (
              <div className="font-14 my-5">
                <div className="block text-sm font-semibold text-gray-700 mb-1">
                  Assigned Numbers
                </div>
                <SelectBar
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder="Select numbers"
                  value={selectedNumbers}
                  onChange={handleNumberChange}
                  options={numberOptions}
                  optionFilterProp="label"
                  showSearch
                />
              </div>
            )}
          </>
        )}
      </Modal>
      <ConfirmationModal
        isLoading={deleteLoading}
        open={isDeleteModalVisible}
        title="Are you sure you want to delete this cover page?"
        description="This action cannot be undone. Deleting the cover page will permanently remove all its content and cannot be recovered."
        onCancel={() => setIsDeleteModalVisible(false)}
        onConfirm={() => onDeleteCoverPage()}
      />
    </>
  );
};

export default EditCoverPageModal;
